import type { ExerciseTemplate } from '@yuan-math/exercises';
import { fraction, toLatex, toText } from '@yuan-math/math';
import { choiceExercise, expr, fractionExercise, leadingTerm, numericExercise, options, signedConstant, template, trap } from './authoring.ts';

/**
 * A table of values, drawn as two rows.
 *
 * Two rows rather than two columns on purpose: the skill being tested is
 * noticing that the x-row does not step by one, and a horizontal row is where
 * a reader actually sees the gaps between consecutive entries.
 */
function xyTableLatex(xs: readonly number[], ys: readonly number[]): string {
  return [
    `\\begin{array}{c|${'c'.repeat(xs.length)}}`,
    `x & ${xs.join(' & ')} \\\\`,
    '\\hline',
    `y & ${ys.join(' & ')}`,
    '\\end{array}',
  ].join(' ');
}

/** Functions, slope and systems — the Grade 8 algebra spine. */
export const FUNCTION_TEMPLATES: ExerciseTemplate[] = [
  template({
    id: 'function-evaluate',
    conceptId: 'functions-intro',
    description: 'Evaluate a function at a value.',
    difficulties: [2, 3],
    build: ({ rng, difficulty }) => {
      const m = rng.int(2, difficulty === 2 ? 6 : 9);
      const c = rng.int(-9, 9);
      const x = rng.int(difficulty === 2 ? 1 : -6, 8);
      const answer = m * x + c;

      return numericExercise({
        prompt: `If f(x) = ${expr(leadingTerm(m), signedConstant(c))}, what is f(${x})?`,
        latex: `f(${x})`,
        value: answer,
        summary: `Substitute ${x} for x: ${m} × ${x} ${signedConstant(c)} = ${answer}.`,
        hint: 'f(x) is not a multiplication. The number in the brackets is what you put in for x.',
        steps: [
          { label: 'Replace every x with the input.', latex: `${m}(${x}) ${signedConstant(c)}` },
          { label: 'Work it out.', latex: `${m * x} ${signedConstant(c)} = ${answer}` },
        ],
        // Reading f(x) as f times x, then adding the constant.
        traps: [...(m * x + c === m * x ? [] : [trap(m * x, 'function-repeated-input')])],
      });
    },
  }),

  template({
    id: 'function-identify',
    conceptId: 'functions-intro',
    description: 'Decide which table of values is a function.',
    difficulties: [3, 4],
    build: ({ rng }) => {
      const a = rng.int(1, 5);
      const b = a + rng.int(1, 4);
      const p = rng.int(1, 9);
      const q = p + rng.int(1, 9);

      const { choices, correctId } = options(
        [
          { label: `(${a}, ${p}), (${b}, ${q}), (${b + 1}, ${p})`, correct: true },
          { label: `(${a}, ${p}), (${a}, ${q}), (${b}, ${p})`, misconceptionId: 'function-repeated-input' },
          { label: `(${b}, ${p}), (${b}, ${q}), (${b}, ${p + 1})`, misconceptionId: 'function-repeated-input' },
          { label: `(${a}, ${p}), (${b}, ${q}), (${a}, ${q + 1})`, misconceptionId: 'function-repeated-input' },
        ],
        rng.shuffle,
      );

      return choiceExercise({
        prompt: 'Which of these sets of (input, output) pairs is a function?',
        choices,
        correctId,
        summary:
          'A function gives exactly one output for each input. Only one of these never repeats an input with a different output — repeating an *output* is perfectly allowed.',
        hint: 'Look at the first number in each pair. Does any input appear twice with different outputs?',
      });
    },
  }),

  template({
    id: 'slope-from-two-points',
    conceptId: 'linear-functions',
    description: 'Find the slope through two points.',
    difficulties: [3, 4],
    build: ({ rng, difficulty }) => {
      const x1 = rng.int(-6, 3);
      const run = rng.int(1, difficulty === 4 ? 5 : 3);
      const x2 = x1 + run;
      const y1 = rng.int(-6, 6);
      const rise = rng.int(1, 9) * (rng.bool() ? 1 : -1);
      const y2 = y1 + rise;
      const slope = fraction(rise, run);

      return fractionExercise({
        prompt: `What is the slope of the line through (${x1}, ${y1}) and (${x2}, ${y2})? Give your answer as a fraction in its simplest form.`,
        figure: {
          kind: 'coordinate-grid',
          min: -8,
          max: 8,
          points: [
            { x: x1, y: y1, label: `(${x1}, ${y1})` },
            { x: x2, y: y2, label: `(${x2}, ${y2})` },
          ],
        },
        value: slope,
        requireSimplified: true,
        summary: `Rise ÷ run = (${y2} − ${y1}) ÷ (${x2} − ${x1}) = ${rise}/${run} = ${toText(slope)}.`,
        hint: 'Slope is rise over run — the change in y divided by the change in x.',
        steps: [
          { label: 'Find the rise.', latex: `${y2} - (${y1}) = ${rise}` },
          { label: 'Find the run.', latex: `${x2} - (${x1}) = ${run}` },
          { label: 'Divide.', latex: `m = ${toLatex(slope)}` },
        ],
        traps: [{ response: `${run}/${rise}`, misconceptionId: 'slope-inverted' }],
      });
    },
  }),

  template({
    id: 'slope-intercept-read',
    conceptId: 'linear-functions',
    description: 'Read the slope or the intercept off an equation.',
    difficulties: [2, 3],
    build: ({ rng }) => {
      const m = rng.int(2, 9) * (rng.bool() ? 1 : -1);
      const c = rng.int(-9, 9);
      // With m = c the two readings coincide and the question tests nothing.
      const slopeWanted = rng.bool();
      const answer = slopeWanted ? m : c;
      const other = slopeWanted ? c : m;

      return numericExercise({
        prompt: `The line y = ${expr(leadingTerm(m), signedConstant(c))} is drawn. What is its ${slopeWanted ? 'slope' : 'y-intercept'}?`,
        latex: expr('y =', leadingTerm(m), signedConstant(c)),
        figure: { kind: 'coordinate-grid', min: -10, max: 10, lines: [{ slope: m, intercept: c, label: 'y' }] },
        value: answer,
        summary: slopeWanted
          ? `In y = mx + c, the number attached to x is the slope, so m = ${m}.`
          : `In y = mx + c, the number on its own is where the line crosses the y-axis, so c = ${c}.`,
        hint: 'One of the two numbers is multiplied by x. That one is the slope; the other is the intercept.',
        steps: [
          { label: 'Compare with y = mx + c.', latex: `m = ${m},\\quad c = ${c}` },
          { label: `Read off the ${slopeWanted ? 'slope' : 'intercept'}.`, latex: `${answer}` },
        ],
        traps: [...(other === answer ? [] : [trap(other, 'slope-intercept-swapped')])],
      });
    },
  }),

  template({
    id: 'linear-value-at',
    conceptId: 'linear-functions',
    description: 'Use a linear rule to predict a value.',
    difficulties: [3, 4],
    build: ({ rng, difficulty }) => {
      const rate = rng.int(2, difficulty === 4 ? 15 : 8);
      const start = rng.int(2, 40);
      const x = rng.int(3, difficulty === 4 ? 20 : 10);
      const answer = rate * x + start;
      const scenario = rng.pick([
        { prompt: `A plumber charges £${start} to come out, plus £${rate} an hour. What is the bill for ${x} hours?`, unit: '£' },
        { prompt: `A tank holds ${start} litres and is filling at ${rate} litres a minute. How much is in it after ${x} minutes?`, unit: 'litres' },
        { prompt: `A plant is ${start} cm tall and grows ${rate} cm a week. How tall is it after ${x} weeks?`, unit: 'cm' },
      ]);

      return numericExercise({
        prompt: scenario.prompt,
        unit: scenario.unit,
        value: answer,
        summary: `The rate is ${rate} per unit and the starting value is ${start}, so y = ${rate}x + ${start} gives ${rate} × ${x} + ${start} = ${answer}.`,
        hint: 'One number is a starting amount and the other is a rate of change. Which one gets multiplied?',
        steps: [
          { label: 'Write the rule.', latex: `y = ${rate}x + ${start}` },
          { label: 'Substitute.', latex: `${rate}(${x}) + ${start} = ${answer}` },
        ],
        // Slope and intercept exchanged: the fixed charge multiplied instead.
        traps: [...(start * x + rate === answer ? [] : [trap(start * x + rate, 'slope-intercept-swapped')])],
      });
    },
  }),

  template({
    id: 'system-solve-substitution',
    conceptId: 'systems-of-equations',
    description: 'Solve a system of two linear equations.',
    difficulties: [4, 5],
    build: ({ rng, difficulty }) => {
      const x = rng.int(1, difficulty === 5 ? 8 : 5);
      const y = rng.int(1, difficulty === 5 ? 8 : 5);
      const a = rng.int(1, 4);
      const b = rng.int(1, 4);
      const c = rng.int(1, 4);
      let d = rng.int(1, 4);
      // Parallel or identical lines have no single intersection to find.
      if (a * d === b * c) d = d + 1;

      const first = a * x + b * y;
      const second = c * x + d * y;

      return numericExercise({
        prompt: 'Solve this system. What is the value of x?',
        latex: `\\begin{cases} ${leadingTerm(a)} + ${leadingTerm(b, 'y')} = ${first} \\\\ ${leadingTerm(c)} + ${leadingTerm(d, 'y')} = ${second} \\end{cases}`,
        value: x,
        summary: `The two lines meet at (${x}, ${y}), so x = ${x}.`,
        hint: 'Eliminate one unknown by combining the equations, or rearrange one and substitute it into the other.',
        steps: [
          { label: 'Rearrange the first equation for y.', latex: `y = \\frac{${first} - ${a}x}{${b}}` },
          { label: 'Substitute into the second and solve.', latex: `x = ${x}` },
          { label: 'Check the point satisfies *both* equations.', latex: `(${x}, ${y})` },
        ],
        // The value of y — the other coordinate, which fits both equations but
        // answers a different question.
        traps: [...(y === x ? [] : [trap(y, 'system-satisfies-one-equation')])],
      });
    },
  }),

  template({
    id: 'system-from-graph',
    conceptId: 'systems-of-equations',
    description: 'Read the solution of a system off a graph.',
    difficulties: [3, 4],
    build: ({ rng }) => {
      const x = rng.int(-4, 4);
      const m1 = rng.int(1, 3) * (rng.bool() ? 1 : -1);
      let m2 = rng.int(1, 3) * (rng.bool() ? 1 : -1);
      if (m2 === m1) m2 = m1 + 1;
      const y = rng.int(-4, 4);
      const c1 = y - m1 * x;
      const c2 = y - m2 * x;

      return numericExercise({
        prompt: 'These two lines are the graphs of a system of equations. What is the x-coordinate of the solution?',
        figure: {
          kind: 'coordinate-grid',
          min: -8,
          max: 8,
          lines: [
            { slope: m1, intercept: c1, label: 'A' },
            { slope: m2, intercept: c2, label: 'B' },
          ],
          points: [{ x, y, label: `(${x}, ${y})` }],
        },
        value: x,
        summary: `The lines cross at (${x}, ${y}), which is the only point lying on both — so that pair solves both equations at once.`,
        hint: 'The solution of a system is the point where the graphs meet, because only there are both equations true.',
        steps: [
          { label: 'Find where the two lines cross.', latex: `(${x}, ${y})` },
          { label: 'Read off the x-coordinate.', latex: `x = ${x}` },
        ],
        traps: [...(y === x ? [] : [trap(y, 'system-satisfies-one-equation')])],
      });
    },
  }),

  /* --------------------------------------------- linear versus nonlinear */
  template({
    id: 'function-linear-rule',
    conceptId: 'comparing-functions',
    description: 'Pick the rule that defines a linear function.',
    difficulties: [2, 3],
    build: ({ rng }) => {
      const m = rng.int(2, 9);
      const c = rng.int(-9, 9);
      // Distinct coefficients, so the four options differ by shape and not by
      // one repeated number a student could pattern-match on.
      const square = rng.int(2, 9);
      const over = rng.int(2, 9);
      const base = rng.int(2, 5);

      const { choices, correctId } = options(
        [
          { latex: expr('y =', leadingTerm(m), signedConstant(c)), correct: true },
          { latex: expr(`y = ${square}x^{2}`, signedConstant(c)), misconceptionId: 'linear-form-misjudged' },
          { latex: `y = \\frac{${over}}{x}`, misconceptionId: 'linear-form-misjudged' },
          { latex: `y = ${base}^{x}`, misconceptionId: 'linear-form-misjudged' },
        ],
        rng.shuffle,
      );

      return choiceExercise({
        prompt: 'Which of these rules defines a linear function?',
        choices,
        correctId,
        summary:
          'A linear function has x on its own, to the first power. Squaring it, dividing by it or raising a number to it all make the graph bend, so equal steps in x stop giving equal steps in y.',
        hint: 'Ask what happens to x. Is it multiplied and added to, or is it squared, underneath a fraction line, or up in the exponent?',
      });
    },
  }),

  template({
    id: 'function-rate-from-table',
    conceptId: 'comparing-functions',
    description: 'Find a rate of change from a table whose x-values step by more than one.',
    difficulties: [3, 4],
    build: ({ rng, difficulty }) => {
      const step = rng.int(2, difficulty === 4 ? 5 : 3);
      const rate = rng.int(2, 9) * (difficulty === 4 && rng.bool() ? -1 : 1);
      const start = rng.int(-3, 3);
      const intercept = rng.int(-8, 8);
      const xs = [start, start + step, start + 2 * step, start + 3 * step];
      const ys = xs.map((x) => rate * x + intercept);

      return numericExercise({
        prompt: 'What is the rate of change of the linear function in this table?',
        latex: xyTableLatex(xs, ys),
        value: rate,
        summary: `y changes by ${ys[1]! - ys[0]!} while x changes by ${step}, so the rate of change is ${ys[1]! - ys[0]!} ÷ ${step} = ${rate}.`,
        hint: 'Look at the x row first. It does not step by one here, so the change in y is not the rate on its own.',
        steps: [
          { label: 'Take one change in y.', latex: `${ys[1]} - (${ys[0]}) = ${ys[1]! - ys[0]!}` },
          { label: 'Take the change in x that produced it.', latex: `${xs[1]} - (${xs[0]}) = ${step}` },
          { label: 'Divide.', latex: `\\frac{${ys[1]! - ys[0]!}}{${step}} = ${rate}` },
        ],
        // The change in y reported without dividing by the change in x.
        traps: [trap(ys[1]! - ys[0]!, 'rate-change-not-per-unit')],
      });
    },
  }),

  template({
    id: 'function-compare-two-forms',
    conceptId: 'comparing-functions',
    description: 'Compare the rates of change of two functions given in different forms.',
    difficulties: [3, 4],
    build: ({ rng }) => {
      const step = rng.int(2, 4);
      const tableRate = rng.int(2, 6);
      /*
       * When the rule is the faster of the two its rate is exactly the table's
       * *undivided* difference, so "they grow at the same rate" is precisely
       * what a student who forgot to divide would conclude — a distractor
       * reachable by a real calculation rather than by elimination.
       */
      const ruleFaster = rng.bool();
      const ruleRate = ruleFaster ? tableRate * step : rng.int(1, tableRate - 1);
      // The slower function starts ahead, so "which is bigger here" and "which
      // grows faster" have different answers.
      const ruleIntercept = ruleFaster ? rng.int(-4, 0) : rng.int(12, 20);
      const tableIntercept = ruleFaster ? rng.int(12, 20) : rng.int(-4, 0);

      const ruleIsA = rng.bool();
      const ruleName = ruleIsA ? 'A' : 'B';
      const tableName = ruleIsA ? 'B' : 'A';

      const xs = [0, step, 2 * step, 3 * step];
      const ys = xs.map((x) => tableRate * x + tableIntercept);

      const { choices, correctId } = options(
        [
          { label: `Function ${ruleFaster ? ruleName : tableName}`, correct: true },
          { label: `Function ${ruleFaster ? tableName : ruleName}`, misconceptionId: 'functions-compared-by-intercept' },
          { label: 'Neither — they grow at the same rate', misconceptionId: 'rate-change-not-per-unit' },
          { label: 'Neither — the table is not a linear function', misconceptionId: 'nonlinear-called-linear' },
        ],
        rng.shuffle,
      );

      const ruleClause = `Function ${ruleName} is ${expr('y =', leadingTerm(ruleRate), signedConstant(ruleIntercept))}.`;
      const tableClause = `Function ${tableName} is given by the table.`;

      return choiceExercise({
        // A always introduced before B, whichever of the two is the equation.
        prompt: `${ruleIsA ? ruleClause : tableClause} ${ruleIsA ? tableClause : ruleClause} Which has the greater rate of change?`,
        latex: xyTableLatex(xs, ys),
        choices,
        correctId,
        summary: `Function ${ruleName} gains ${ruleRate} per unit of x, read straight from its equation. In the table x steps by ${step}, so its rate is ${tableRate * step} ÷ ${step} = ${tableRate}. ${ruleFaster ? ruleName : tableName} is the faster of the two, even though the other one starts ahead.`,
        hint: 'Reduce both to a rate per single unit of x before comparing. Which one is ahead at the start is a different question.',
      });
    },
  }),
];
